import { useMemo, CSSProperties } from 'react';
import { useVideoConfig } from 'remotion';
import { VideoElement } from '../../MainPlayer/types/Element/VideoElement';
import VideoElementComponent from '../VideoElementComponent/VideoElementComponent';
import { VideoCropPosition } from '../../MainPlayer/types/Scene';
import { getCropStyles } from './cropUtils';
import { VideoCropPositionStyle } from '../../MainPlayer/types/Segment/LayoutChange';
import { useRemotionEnvVariables } from '../../MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionEnvVariables';

function VideoInCompositeSegment({
  video,
  canEditSpeakerLabel,
  videoIndex,
  finalConfig,
  hasScreenshareAtCurrentFrame,
  commonStartFromForElements,
  canManuallyReposition,
  preventOverlappingWithOtherCrops,
  cropInformation,
  muted,
  layoutChangeFrame,
  allCropInformation,
  cropStyles,
  animationStyles,
  compositeSegmentId
}: {
  video: VideoElement;
  canEditSpeakerLabel: boolean;
  videoIndex: number;
  finalConfig: any[];
  hasScreenshareAtCurrentFrame: boolean;
  commonStartFromForElements: number;
  canManuallyReposition: boolean;
  cropInformation: VideoCropPosition | null;
  allCropInformation?: Record<string, VideoCropPosition>;
  muted: boolean;
  layoutChangeFrame?: number;
  preventOverlappingWithOtherCrops?: boolean;
  cropStyles?: VideoCropPositionStyle;
  animationStyles?: CSSProperties;
  compositeSegmentId: string;
}) {
  const { width: playerWidth, height: playerHeight } = useVideoConfig();

  const { isGeneratingThumbnail } = useRemotionEnvVariables();
  const isVideoHidden = videoIndex === -1;

  // Calculate styles based on video index and configuration
  const newStyle: CSSProperties = isVideoHidden ? {} : (finalConfig[videoIndex]?.style as CSSProperties);
  const newContainerStyle: CSSProperties = useMemo(() => {
    return isVideoHidden
      ? // width, height 0 is to reduce the size of the video element and improve rendering performance
        { zIndex: -1, visibility: 'hidden', width: '1px', height: '1px' }
      : (finalConfig[videoIndex]?.containerStyle as CSSProperties);
  }, [finalConfig, isVideoHidden, videoIndex]);

  const playerAspectRatio = useMemo(() => {
    return playerWidth / playerHeight;
  }, [playerWidth, playerHeight]);

  const computedCropStyles = useMemo(() => {
    if (cropStyles) {
      return cropStyles;
    }
    const allOtherCropInformation = preventOverlappingWithOtherCrops
      ? Object.keys(allCropInformation || {})
          .filter(key => key !== video.id)
          .reduce((acc, key) => {
            acc[key] = allCropInformation?.[key];
            return acc;
          }, {})
      : undefined;
    const finalCropStyles = getCropStyles(
      cropInformation,
      newContainerStyle,
      playerAspectRatio,
      allOtherCropInformation,
      true,
      video.aspectRatio
    );

    return finalCropStyles;
  }, [
    allCropInformation,
    cropInformation,
    cropStyles,
    newContainerStyle,
    playerAspectRatio,
    preventOverlappingWithOtherCrops,
    video.aspectRatio,
    video.id
  ]);

  if (isVideoHidden && muted) {
    return null;
  }

  if (isGeneratingThumbnail && isVideoHidden) {
    return null;
  }

  return (
    <>
      <VideoElementComponent
        element={{
          ...video,
          style: {
            ...video.style,
            ...newStyle,
            ...computedCropStyles,
            ...(hasScreenshareAtCurrentFrame && videoIndex === 0 && video.name.includes('screenshare')
              ? { objectFit: 'contain' }
              : {})
          } as CSSProperties,
          containerStyle: {
            ...video.containerStyle,
            ...newContainerStyle
          } as CSSProperties,
          fromFrame: commonStartFromForElements,
          details: {
            ...video.details,
            ...(muted
              ? {
                  volume: [
                    {
                      value: 0,
                      fromFrame: 0,
                      toFrame: video.toFrame
                    }
                  ]
                }
              : {})
          }
        }}
        disableMediaFragmentHints={true}
        ignoreEndAt
        compositeSegmentId={compositeSegmentId}
        cropInformation={cropInformation || undefined}
        canEditSpeakerLabel={canEditSpeakerLabel}
        canManuallyReposition={canManuallyReposition}
        layoutChangeFrame={layoutChangeFrame}
        animationStylesUserVideo={animationStyles}
      />
    </>
  );
}

export default VideoInCompositeSegment;
